/**
 * 予実分析の純粋な集計ロジック。
 *
 * 画面 (index.tsx) から切り出した React 非依存の純関数群。
 * 「実データ → 画面表示値」の対応を unit テストで完全検証できるようにするための
 * 単一の真実 (single source of truth)。index.tsx はここを import して使う。
 *
 * 集計仕様 (2026-05-30 確定):
 * - 前月まで: 当月を含めず、確定済みの先頭月のみ集計 (時系列 prefix = completedCount)。
 * - 両方入力のみ: 物件 × 月 × 収入/支出 の各セルで「予算≠0 かつ 実績≠0」の月だけを
 *   予算合計・実績合計の双方に加算する (金額 0 は未入力扱い)。
 * - 今月の縦線: 表示中の会計年度に「今月」が含まれるときのみ index を返す (currentMonthIndex)。
 */

import type {
  BudgetAnalysisMonthly,
  BudgetAnalysisProperty,
} from "@/store/budgetAnalysisSlice";

export type Metric = "noi" | "income" | "expense";
export type FinancialValueTone = "negative" | "neutral" | "unavailable";

export function resolveFinancialValueTone(
  value: number,
  available = true,
): FinancialValueTone {
  if (!available) return "unavailable";
  return value < 0 ? "negative" : "neutral";
}

export interface GatedTotals {
  incomeBudget: number;
  incomeActual: number;
  expenseBudget: number;
  expenseActual: number;
  noiBudget: number;
  noiActual: number;
}

export interface MonthProgress {
  /** 当月より前で確定済みの先頭からの月数 (前月まで集計の件数) */
  completedCount: number;
  /** 「今月」が表示中の会計年度内にある場合のその index、無ければ -1 */
  currentMonthIndex: number;
}

export interface NoiAvailability {
  noiBudgetAvailable: boolean;
  noiActualAvailable: boolean;
  noiDiffAvailable: boolean;
}

export interface ComparableNoi {
  actual: number;
  budget: number;
  diff: number;
  available: boolean;
}

const isMetricEntered = (
  month: BudgetAnalysisMonthly,
  field:
    | "income_budget"
    | "income_actual"
    | "expense_budget"
    | "expense_actual",
): boolean => {
  const entered = month[`${field}_entered` as keyof BudgetAnalysisMonthly];
  return typeof entered === "boolean" ? entered : month[field] !== 0;
};

/**
 * 対象期間にNOI予算・実績を構成できる入力があるか判定する。
 * 明示的な0円は entered=true として計算可能、未入力は計算不能とする。
 */
export function aggregateNoiAvailability(
  props: BudgetAnalysisProperty[],
  monthCount: number,
): NoiAvailability {
  let incomeBudgetEntered = false;
  let incomeActualEntered = false;
  let expenseBudgetEntered = false;
  let expenseActualEntered = false;

  for (const property of props) {
    const count = Math.min(monthCount, property.monthly.length);
    for (let index = 0; index < count; index++) {
      const month = property.monthly[index];
      if (!month) continue;
      incomeBudgetEntered ||= isMetricEntered(month, "income_budget");
      incomeActualEntered ||= isMetricEntered(month, "income_actual");
      expenseBudgetEntered ||= isMetricEntered(month, "expense_budget");
      expenseActualEntered ||= isMetricEntered(month, "expense_actual");
    }
  }

  const noiBudgetAvailable = incomeBudgetEntered && expenseBudgetEntered;
  const noiActualAvailable = incomeActualEntered && expenseActualEntered;
  return {
    noiBudgetAvailable,
    noiActualAvailable,
    noiDiffAvailable: noiBudgetAvailable && noiActualAvailable,
  };
}

/**
 * NOI差異を物件単位で成立判定して合算する。
 * 予算だけ／実績だけの物件をグループ差異へ混ぜると比較母集団がずれるため、
 * 両方のNOIが成立した物件だけを対象にする。
 */
export function aggregateComparableNoi(
  props: BudgetAnalysisProperty[],
  monthCount: number,
): ComparableNoi {
  let actual = 0;
  let budget = 0;
  let available = false;

  for (const property of props) {
    const availability = aggregateNoiAvailability([property], monthCount);
    if (!availability.noiDiffAvailable) continue;
    const totals = aggregateTotals([property], monthCount);
    actual += totals.noiActual;
    budget += totals.noiBudget;
    available = true;
  }

  return { actual, budget, diff: actual - budget, available };
}

/**
 * 物件群を月次で生集計する (ゲートなし・全月)。
 * 月次推移グラフ / スパークライン用。
 */
export function aggregateMonthly(
  props: BudgetAnalysisProperty[],
  monthKeys: string[],
): BudgetAnalysisMonthly[] {
  return monthKeys.map((mk, i) => {
    let income_budget = 0;
    let income_actual = 0;
    let expense_budget = 0;
    let expense_actual = 0;
    for (const p of props) {
      const m = p.monthly[i];
      if (!m) continue;
      income_budget += m.income_budget || 0;
      income_actual += m.income_actual || 0;
      expense_budget += m.expense_budget || 0;
      expense_actual += m.expense_actual || 0;
    }
    return {
      month_key: mk,
      income_budget,
      income_actual,
      expense_budget,
      expense_actual,
    };
  });
}

/**
 * 物件群の累計を全月そのまま合算する (ゲートなし)。
 * 予実分析は集計結果なので、予算・実績は入力されていればそのまま足す。
 * 予算が未入力 (0) でも実績は反映される (予算ゲートで実績が消えない)。
 * 月キーは会計年度順 (時系列) なので monthCount は先頭からの件数で表せる。
 */
export function aggregateTotals(
  props: BudgetAnalysisProperty[],
  monthCount: number,
): GatedTotals {
  let incomeBudget = 0;
  let incomeActual = 0;
  let expenseBudget = 0;
  let expenseActual = 0;
  for (const p of props) {
    const n = Math.min(monthCount, p.monthly.length);
    for (let i = 0; i < n; i++) {
      const m = p.monthly[i];
      if (!m) continue;
      incomeBudget += m.income_budget || 0;
      incomeActual += m.income_actual || 0;
      expenseBudget += m.expense_budget || 0;
      expenseActual += m.expense_actual || 0;
    }
  }
  return {
    incomeBudget,
    incomeActual,
    expenseBudget,
    expenseActual,
    noiBudget: incomeBudget - expenseBudget,
    noiActual: incomeActual - expenseActual,
  };
}

/**
 * スパークライン用の月次 NOI 実績トレンド (= Σ 収入実績 - 支出実績)。
 *
 * 予算の有無ではゲートしない。実績が入力されていれば反映するので、合計NOIがあるのに
 * 横棒になる (= ゲートで該当月が1つ以下に潰れる) 問題を避け、実際のトレンドを描く。
 * 末尾の「実績が無い月」だけは切り落とす (右下がりの誤解防止)。
 * 全月実績なしなら空配列を返し、スパークラインは描かれない。
 *
 * 注: 末尾トリムするため、系列の合計は aggregateTotals(...).noiActual とは
 * 必ずしも一致しない (スパークラインは傾向の可視化が目的)。
 */
export function aggregateMonthlyNoiActualTrend(
  props: BudgetAnalysisProperty[],
  monthCount: number,
): number[] {
  const out: number[] = [];
  let lastWithData = -1;
  for (let i = 0; i < monthCount; i++) {
    let v = 0;
    let hasData = false;
    for (const p of props) {
      const m = p.monthly[i];
      if (!m) continue;
      const ia = m.income_actual || 0;
      const ea = m.expense_actual || 0;
      v += ia - ea;
      if (ia !== 0 || ea !== 0) hasData = true;
    }
    out.push(v);
    if (hasData) lastWithData = i;
  }
  return out.slice(0, lastWithData + 1);
}

/** 月次データから指標 (noi/income/expense) × 予算/実績 の値を取り出す */
export function pickMetric(
  m: BudgetAnalysisMonthly,
  metric: Metric,
  kind: "budget" | "actual",
): number {
  if (metric === "income") {
    return kind === "budget" ? m.income_budget : m.income_actual;
  }
  if (metric === "expense") {
    return kind === "budget" ? m.expense_budget : m.expense_actual;
  }
  // noi = income - expense
  if (kind === "budget") return m.income_budget - m.expense_budget;
  return m.income_actual - m.expense_actual;
}

/**
 * 表示中の会計年度における「前月までの確定月数」と「今月の index」を求める。
 *
 * 会計年度は決算月の翌月開始。月キーは会計年度順 (時系列) に並ぶ。
 * 各月キーの暦年は、月番号 >= 会計年度開始月なら年度開始年 fy、そうでなければ fy + 1。
 *
 * - completedCount: 当月より前で確定済みの先頭月数 (prefix)。
 *   今年度=当月の直前まで / 過去年度=全 12 / 未来年度=0。
 * - currentMonthIndex: 「今月」が年度内にあればその index、無ければ -1
 *   (= 今年度表示時のみ >= 0。今月の縦線はこのときだけ出す)。
 */
export function computeMonthProgress(
  monthKeys: string[],
  fiscalYearStartMonth: number,
  fiscalYear: number,
  currentYear: number,
  currentMonth: number,
): MonthProgress {
  let completedCount = 0;
  let currentMonthIndex = -1;
  let prefixOpen = true;
  for (let i = 0; i < monthKeys.length; i++) {
    const monthNum = parseInt(monthKeys[i], 10);
    const calYear =
      monthNum >= fiscalYearStartMonth ? fiscalYear : fiscalYear + 1;
    const isCompleted =
      calYear < currentYear ||
      (calYear === currentYear && monthNum < currentMonth);
    if (isCompleted && prefixOpen) completedCount++;
    else prefixOpen = false;
    if (calYear === currentYear && monthNum === currentMonth) {
      currentMonthIndex = i;
    }
  }
  return { completedCount, currentMonthIndex };
}

/**
 * ポートフォリオ全体の月次推移グラフ用データ。
 * - budget: 年間通して表示 (年間計画)。
 * - actual: 予実分析は集計結果なので 0/未入力も実データ (0) として全月に点を出す。
 *   予算も実績も 0 の月でも丸 (マーク) を出したい要件のため null にはしない。
 */
export function buildPortfolioChartData(
  portfolioAgg: BudgetAnalysisMonthly[],
  chartMetric: Metric,
): { budget: number[]; actual: (number | null)[] } {
  return {
    budget: portfolioAgg.map((m) => pickMetric(m, chartMetric, "budget")),
    actual: portfolioAgg.map((m) => pickMetric(m, chartMetric, "actual")),
  };
}
